import { useState, useEffect } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { formatMoeda } from '../../utils/fmt';
import * as api from '../../api/painelFinanceiro';

function StatCard({ valor, label, cor }) {
  return (
    <div className="cjc-stat-card h-100">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color: cor || 'var(--cjc-primary)' }}>{valor ?? '—'}</div>
    </div>
  );
}

function BarraMensal({ dados }) {
  if (!dados || dados.length === 0) {
    return <div className="text-muted p-3" style={{ fontSize: 13 }}>Sem pedidos nos últimos 12 meses.</div>;
  }
  const max = Math.max(...dados.map(d => d.valor), 1);
  return (
    <div className="d-flex align-items-end gap-2" style={{ height: 160, padding: '12px 8px 0' }}>
      {dados.map(d => (
        <div key={d.mes} className="d-flex flex-column align-items-center justify-content-end" style={{ flex: 1, height: '100%' }}>
          <div className="text-muted" style={{ fontSize: 11 }}>{formatMoeda(d.valor)}</div>
          <div style={{
            width: '70%',
            minHeight: 3,
            height: `${Math.max((d.valor / max) * 100, 3)}%`,
            background: 'var(--cjc-primary)',
            borderRadius: 'var(--cjc-radius) var(--cjc-radius) 0 0',
          }} title={`${d.mes}: ${d.total} pedido(s)`} />
          <div className="text-muted mt-1" style={{ fontSize: 11 }}>{d.mes.slice(5)}/{d.mes.slice(2, 4)}</div>
        </div>
      ))}
    </div>
  );
}

function RankingTabela({ titulo, dados, nomeCol }) {
  if (!dados || dados.length === 0) {
    return <div className="text-muted p-3" style={{ fontSize: 13 }}>Nenhum pedido registrado ainda.</div>;
  }
  const max = Math.max(...dados.map(d => d.valor), 1);
  return (
    <table className="table table-hover mb-0">
      <thead><tr><th>{titulo}</th><th>Pedidos</th><th>Valor</th><th style={{ width: '35%' }}></th></tr></thead>
      <tbody>
        {dados.map(d => (
          <tr key={d[nomeCol]}>
            <td className="fw-semibold" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d[nomeCol]}</td>
            <td>{d.total}</td>
            <td>{formatMoeda(d.valor)}</td>
            <td>
              <div style={{ background: 'var(--cjc-gray-100)', borderRadius: 'var(--cjc-radius)', height: 8 }}>
                <div style={{ width: `${(d.valor / max) * 100}%`, height: '100%', background: 'var(--cjc-accent)', borderRadius: 'var(--cjc-radius)' }} />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function PainelFinanceiro() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.getPainelFinanceiro()
      .then(r => setDados(r.data))
      .catch(() => setErro('Erro ao carregar o painel financeiro. Tente novamente.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;
  if (erro) return <div className="alert alert-danger">{erro}</div>;
  if (!dados) return null;

  const global = dados.escopo === 'global';

  return (
    <div>
      <PageHeader
        titulo="Painel Financeiro"
        subtitulo={global ? 'Toda a plataforma (todas as empresas)' : `Empresa: ${dados.nome_empresa || '—'}`}
      />

      <div className="row g-3">
        <div className="col-6 col-lg-3"><StatCard valor={dados.total} label="Total de Pedidos" /></div>
        <div className="col-6 col-lg-3"><StatCard valor={dados.abertos} label="Em Aberto" /></div>
        <div className="col-6 col-lg-3"><StatCard valor={dados.finalizados} label="Finalizados" cor="#1a7a3d" /></div>
        <div className="col-6 col-lg-3"><StatCard valor={dados.cancelados} label="Cancelados" cor="var(--cjc-accent)" /></div>
      </div>
      <div className="row g-3 mt-1">
        <div className="col-6 col-lg-3"><StatCard valor={formatMoeda(dados.valor_total_finalizado)} label="Valor Total Finalizado" /></div>
        <div className="col-6 col-lg-3"><StatCard valor={formatMoeda(dados.ticket_medio)} label="Ticket Médio" /></div>
      </div>

      <div className="row g-3 mt-1">
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header">Faturamento por Mês (últimos 12 meses)</div>
            <div className="card-body">
              <BarraMensal dados={dados.por_mes} />
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header">{global ? 'Ranking por Empresa' : 'Top Clientes'}</div>
            <div className="card-body p-0">
              {global
                ? <RankingTabela titulo="Empresa" dados={dados.por_empresa} nomeCol="empresa" />
                : <RankingTabela titulo="Cliente" dados={dados.top_clientes} nomeCol="cliente" />}
            </div>
          </div>
        </div>
      </div>

      {global && dados.top_clientes?.length > 0 && (
        <div className="row g-3 mt-1">
          <div className="col-12">
            <div className="card">
              <div className="card-header">Top Clientes (toda a plataforma)</div>
              <div className="card-body p-0">
                <RankingTabela titulo="Cliente" dados={dados.top_clientes} nomeCol="cliente" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
